import React, { createContext, useContext, useMemo, useReducer } from "react";

import { EMPTY_FILTERS, filterReducer, isActive, isSelected } from "./filters";

/*
  The page's filter store: context + useReducer, ported from the reference build. It is page-scoped on purpose:
  the selection is ephemeral, synchronous, and the URL is its persisted source of truth, so it does not belong in
  the global redux store (server data does; see redux/sagas/funnelFuelDashboard.js).

  Panels never filter. The page applies the filters once (memoised) and passes the result down; panels only call
  `toggle(selection)` with the server-provided `{ dimension, values[] }` and read `isSelected(selection)`.
*/

const FilterContext = createContext(null);

export const FilterProvider = ({ initialFilters = EMPTY_FILTERS, children }) => {
  const [filters, dispatch] = useReducer(filterReducer, initialFilters);

  const value = useMemo(() => ({
    filters,
    active: isActive(filters),
    isSelected: (selection) => isSelected(filters, selection),
    toggle: (selection) => dispatch({ type: "toggle", selection }),
    remove: (dimension, value) => dispatch({ type: "remove", dimension, value }),
    clear: () => dispatch({ type: "clear" })
  }), [filters]);

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
};

export const useFilters = () => {
  const context = useContext(FilterContext);
  if (!context) { throw new Error("useFilters must be used inside <FilterProvider>"); }

  return context;
};
